import React, { FC, useState, useEffect, useContext } from 'react';
import { ProfileSection } from './ProfileSection';
import { sessionStore } from 'src/store/sessionStore';
import { useUpdateTrainerProfile } from 'src/api/mutations/useUpdateTrainerProfile';
import { ProfileEditContext } from '../ProfileEditContext';
import './LocationFacilitySection.scss';
import { ReactComponent as LocationIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/profile/components/location.svg';
import { ReactComponent as FacilityIcon } from '../../../../../../../assets/images/onboarding-trainer/dashboard/profile/components/facility.svg';

export const LocationFacilitySection: FC = () => {
  const { currentUser } = sessionStore();
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [location, setLocation] = useState(currentUser?.location || '');
  const [facility, setFacility] = useState(currentUser?.facility || '');
  const { mutateAsync: updateProfileBackend, isPending } = useUpdateTrainerProfile();

  const { globalEditAction, registerEditState } = useContext(ProfileEditContext);

  useEffect(() => {
    if (globalEditAction.count > 0) {
      if (globalEditAction.type === 'OPEN') {
        setIsEditing(true);
      } else if (globalEditAction.type === 'CLOSE') {
        setIsEditing(false);
      }
    }
  }, [globalEditAction]);

  useEffect(() => {
    registerEditState('locationFacility', isEditing);
  }, [isEditing, registerEditState]);

  useEffect(() => {
    if (currentUser && !isEditing) {
      setLocation(currentUser.location || '');
      setFacility(currentUser.facility || '');
    }
  }, [currentUser, isEditing]);

  const handleSave = async () => {
    if (!currentUser) return;

    const newErrors: Record<string, string> = {};

    if (location.length > 200) newErrors.location = 'Location must be under 200 characters';
    if (facility.length > 200) newErrors.facility = 'Facility must be under 200 characters';

    const trimmedLocation = location.trim();
    const trimmedFacility = facility.trim();

    if (!trimmedLocation) newErrors.location = 'Location is required';
    if (!trimmedFacility) newErrors.facility = 'Primary facility is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    setLocation(trimmedLocation);
    setFacility(trimmedFacility);

    try {
      const uid = currentUser?.uid || '';
      await updateProfileBackend({
        uid,
        data: { location: trimmedLocation, facility: trimmedFacility },
      });
      setIsEditing(false);
    } catch (error) {
      console.error(error);
    }
  };

  const viewContent = (
    <div className="location-facility-view">
      <div className="location-item">
        <div className="icon-wrapper">{<LocationIcon />}</div>
        <div className="location-details">
          <p className="label">LOCATION</p>
          <p className="value">{currentUser?.location || 'No location provided.'}</p>
        </div>
      </div>

      <div className="location-item">
        <div className="icon-wrapper">{<FacilityIcon />}</div>
        <div className="location-details">
          <p className="label">PRIMARY FACILITY</p>
          <p className="value">{currentUser?.facility || 'No primary facility provided.'}</p>
        </div>
      </div>
    </div>
  );

  const editContent = (
    <div className="location-facility-edit">
      <div className="form-group">
        <label>Location</label>
        <input
          type="text"
          value={location}
          maxLength={200}
          onChange={(e) => {
            setLocation(e.target.value);
            if (errors.location) setErrors((prev) => ({ ...prev, location: '' }));
          }}
          onBlur={(e) => setLocation(e.target.value.trim())}
          placeholder="Los Angeles, CA"
          className={errors.location ? 'error-border' : ''}
        />
        {errors.location && <p className="error-message">{errors.location}</p>}
        {location.length >= 200 && (
          <small
            style={{
              color: '#E44E4E',
              alignSelf: 'flex-end',
              marginTop: 4,
            }}
          >
            {location.length}/200
          </small>
        )}
      </div>

      <div className="form-group">
        <label>Primary Facility</label>
        <input
          type="text"
          value={facility}
          maxLength={200}
          onChange={(e) => {
            setFacility(e.target.value);
            if (errors.facility) setErrors((prev) => ({ ...prev, facility: '' }));
          }}
          onBlur={(e) => setFacility(e.target.value.trim())}
          placeholder="Gold's Gym Venice"
          className={errors.facility ? 'error-border' : ''}
        />
        {errors.facility && <p className="error-message">{errors.facility}</p>}
        {facility.length >= 200 && (
          <small
            style={{
              color: '#E44E4E',
              alignSelf: 'flex-end',
              marginTop: 4,
            }}
          >
            {facility.length}/200
          </small>
        )}
      </div>
    </div>
  );

  return (
    <ProfileSection
      title="Location & Facility"
      className="align-items-center career-highlights-centificate-header"
      isEditing={isEditing}
      isSaving={isPending}
      onEditToggle={setIsEditing}
      onSave={handleSave}
      onCancel={() => {
        if (currentUser) {
          setLocation(currentUser.location || '');
          setFacility(currentUser.facility || '');
          setErrors({});
        }
      }}
      editContent={editContent}
    >
      {viewContent}
    </ProfileSection>
  );
};
